import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Zap, Tag, MessageSquare, Shield } from 'lucide-react';
import { useTheme } from '../ThemeContext';

export const TriggerNode = memo(({ data, selected }) => {
  const { isDark } = useTheme();
  const isFallback = data.triggerType === 'FALLBACK';
  const keywords = data.keywords || ['hi', 'hello'];

  return (
    <div className={`w-72 rounded-xl border transition-all ${
      isDark ? 'bg-[#1a1b1e] border-slate-800 shadow-xl' : 'bg-white border-slate-200/90 shadow-xs'
    } ${
      selected ? 'border-[#dc2742] ring-2 ring-[#dc2742]/30 shadow-md' : (isDark ? 'hover:border-slate-700' : 'hover:border-slate-300')
    }`}>
      {/* Node Header */}
      <div className={`flex items-center justify-between rounded-t-xl px-3.5 py-2.5 border-b ${
        isDark
          ? 'bg-gradient-to-r from-purple-900/60 to-purple-800/40 border-purple-500/20'
          : 'bg-gradient-to-r from-purple-50 to-fuchsia-50 border-purple-200'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`flex h-6 w-6 items-center justify-center rounded-lg ${
            isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'
          }`}>
            {isFallback ? <Shield className="h-3.5 w-3.5 text-purple-400" /> : <Zap className="h-3.5 w-3.5" />}
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider ${
            isDark ? 'text-purple-200' : 'text-purple-900'
          }`}>
            {isFallback ? 'DEFAULT / FALLBACK' : (data.label || 'Flow Trigger')}
          </span>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
          isFallback
            ? (isDark ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-800 border border-amber-200')
            : (isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700')
        }`}>
          {isFallback ? 'CATCH-ALL' : 'START'}
        </span>
      </div>

      {/* Node Body */}
      <div className={`p-3.5 text-xs space-y-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
        {isFallback ? (
          <div className="space-y-1.5 py-0.5">
            <div className={`flex items-center gap-1.5 font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
              <Shield className="h-3.5 w-3.5 text-purple-500" />
              <span>Catch-All Fallback:</span>
            </div>
            <p className={`text-[11px] leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Triggers when customer sends any message not matched by your other active flows.
            </p>
          </div>
        ) : (
          <>
            <div className={`flex items-center gap-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <MessageSquare className="h-3.5 w-3.5 text-purple-500" />
              <span>When customer sends keyword:</span>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {keywords.map((kw, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-mono font-medium border ${
                    isDark
                      ? 'bg-purple-950/60 border-purple-800/50 text-purple-200'
                      : 'bg-purple-50 border-purple-200 text-purple-800'
                  }`}
                >
                  <Tag className="h-2.5 w-2.5 text-purple-500" />
                  "{kw}"
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className={`!h-3.5 !w-3.5 !border-2 !bg-purple-500 hover:!scale-125 transition-transform ${
          isDark ? '!border-slate-900' : '!border-white'
        }`}
      />
    </div>
  );
});
